// AuthContext.js - FIXED VERSION with proper logout handling
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Session storage keys
const STORAGE_KEYS = {
  USER_DATA: 'auth_user_data',
  USER_ROLE: 'auth_user_role',
  LAST_AUTH_CHECK: 'auth_last_check',
  AUTH_STATE: 'auth_state',
  RATE_LIMIT_BACKOFF: 'rate_limit_backoff',
  MANAGER_INFO: 'auth_manager_info'
};

// Role-based redirects - SUPERADMIN FIRST
const ROLE_REDIRECTS = {
  'Superadmin': '/Admindashboard',  
  'SUPERADMIN': '/Admindashboard', 
  'CEO': '/Attendance',
  'HR': '/Hrdashboard',
  'Manager': '/managerdashboard',
  'MANAGER': '/managerdashboard',
  'ADMIN': '/Attendance',
  'Employee': '/Worker',
  'EMPLOYEE':        '/Worker',
  'Consultant': '/Timesheet',
  'CONSULTANT':      '/Timesheet',
  'NETWORK MANAGER': '/NetworkManager',
  'NETWORK_MANAGER': '/NetworkManager',
  'HOUSEKEEPING': '/Worker',
};

// Rate limit configuration
const RATE_LIMIT_CONFIG = {
  MAX_RETRIES: 2,
  INITIAL_DELAY: 2000,
  MAX_DELAY: 30000,
  BACKOFF_MULTIPLIER: 2,
  COOLDOWN_PERIOD: 60000,
  CHECK_INTERVAL: 5000
};

// Inactivity timeout configuration - 5 minutes
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds suganya...

// Backend URL determination with production detection
const getBackendUrl = () => {
  const hostname = window.location.hostname;

  // Staging
  if (hostname === 'portalstaging.manurevasolutions.com') {
    return 'https://api.leave-management.manurevasolutions.com';
  }

 

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_API_URL || 'http://localhost:5000';
  }

  // Unknown host: use the override if given, otherwise fail loudly
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  console.error(`getBackendUrl: unknown host "${hostname}"`);
  return 'http://localhost:5000';
};

const BACKEND_URL = getBackendUrl();
console.log("Backend URL:", BACKEND_URL);

// 🔥 CACHE FIX (runs once, as soon as any page imports AuthContext)
// 1. Every request to the API skips the browser HTTP cache. `cache: 'no-store'`
//    never READS a cached copy either, so responses cached earlier (e.g. another
//    user's leave list) can no longer be shown. Covers every component's fetch().
// 2. Remove any old service worker / Cache Storage that could serve stale data.
if (typeof window !== 'undefined' && !window.__apiNoStorePatched) {
  window.__apiNoStorePatched = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isApi = url.startsWith(BACKEND_URL) || url.startsWith('/api/') || url.startsWith('/auth/');
    if (isApi) {
      init = { ...init, cache: 'no-store' };
    }
    return nativeFetch(input, init);
  };

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then(regs => regs.forEach(r => r.unregister()))
      .catch(() => {});
  }
  if (window.caches && caches.keys) {
    caches.keys().then(keys => keys.forEach(k => caches.delete(k))).catch(() => {});
  }
}

const useAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    userRole: null,
    isLoading: true,
    error: null,
    initialized: false,
    managerId: null,
    managerName: null,
    managerEmail: null,
    teamSize: 0,
    canApproveLeaves: false
  });

  const authCheckInProgress = useRef(false);
  const lastAuthCheck = useRef(0);
  const roleCache = useRef(null);
  const retryTimeouts = useRef([]);
  const rateLimitBackoff = useRef(0);
  const isLoggingOut = useRef(false); // 🔥 NEW: Track logout state
  const inactivityTimer = useRef(null);
  const lastActivityTime = useRef(Date.now());
  const lastUserIdRef = useRef(null); // id of the user this tab is currently showing

  // 🔥 CRITICAL FIX: Check for logout FIRST, before anything else
  // Around line 140 - REPLACE entire useEffect
// 🔥 NEW: Initialize without loading persisted state - Always start fresh
  useEffect(() => {
    console.log('🆕 Starting fresh session - no persistence');
    
    // Clear any existing session data
    sessionStorage.clear();
    
    // Check authentication status
    checkAuthStatus();
    
    // Set up beforeunload to clear everything on tab close
    const handleBeforeUnload = () => {
      console.log('🚪 Tab closing - clearing all data');
      sessionStorage.clear();
      localStorage.clear();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // 🔥 NEW: Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    lastActivityTime.current = Date.now();
    
    // Clear existing timer
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    
    // Set new timer for 5 minutes
    inactivityTimer.current = setTimeout(() => {
      console.log('⏰ Inactivity timeout - logging out');
      handleLogout();
    }, INACTIVITY_TIMEOUT);
    
    console.log('⏱️ Inactivity timer reset');
  }, []);

  // 🔥 NEW: Track user activity
  useEffect(() => {
    if (!authState.isAuthenticated) {
      return;
    }
    
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    const handleActivity = () => {
      resetInactivityTimer();
    };
    
    // Set initial timer
    resetInactivityTimer();
    
    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });
    
    // Handle tab visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('👁️ Tab hidden - starting inactivity countdown');
        // Tab is hidden, timer continues
      } else {
        console.log('👁️ Tab visible - resetting timer');
        resetInactivityTimer();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, [authState.isAuthenticated, resetInactivityTimer]);

  // Load persisted auth state
  const loadPersistedState = () => {
    // Don't load state if we're logging out
    if (isLoggingOut.current) {
      console.log('⏭️ Skipping state load - logout in progress');
      return false;
    }
    
    try {
      // Check for rate limit backoff
      const backoffUntil = sessionStorage.getItem(STORAGE_KEYS.RATE_LIMIT_BACKOFF);
      if (backoffUntil) {
        const backoffTime = parseInt(backoffUntil);
        if (Date.now() < backoffTime) {
          console.log(`⏳ Rate limit backoff active until ${new Date(backoffTime)}`);
          rateLimitBackoff.current = backoffTime;
          
          const savedState = sessionStorage.getItem(STORAGE_KEYS.AUTH_STATE);
          const savedRole = sessionStorage.getItem(STORAGE_KEYS.USER_ROLE);
          const savedUser = sessionStorage.getItem(STORAGE_KEYS.USER_DATA);
          const savedManagerInfo = sessionStorage.getItem(STORAGE_KEYS.MANAGER_INFO);
          
          if (savedState && savedRole && savedUser) {
            const parsedState = JSON.parse(savedState);
            const parsedUser = JSON.parse(savedUser);
            const parsedManagerInfo = savedManagerInfo ? JSON.parse(savedManagerInfo) : {};
            
            setAuthState({
              ...parsedState,
              user: parsedUser,
              userRole: savedRole,
              isLoading: false,
              initialized: true,
              error: 'Rate limited - please wait before refreshing',
              ...parsedManagerInfo
            });
            return true;
          }
        } else {
          sessionStorage.removeItem(STORAGE_KEYS.RATE_LIMIT_BACKOFF);
        }
      }

      // Normal restore logic
      const savedState = sessionStorage.getItem(STORAGE_KEYS.AUTH_STATE);
      const savedRole = sessionStorage.getItem(STORAGE_KEYS.USER_ROLE);
      const savedUser = sessionStorage.getItem(STORAGE_KEYS.USER_DATA);
      const savedManagerInfo = sessionStorage.getItem(STORAGE_KEYS.MANAGER_INFO);
      const lastCheck = sessionStorage.getItem(STORAGE_KEYS.LAST_AUTH_CHECK);

      if (savedState && savedRole && savedUser) {
        const parsedState = JSON.parse(savedState);
        const parsedUser = JSON.parse(savedUser);
        const parsedManagerInfo = savedManagerInfo ? JSON.parse(savedManagerInfo) : {};
        const checkTime = parseInt(lastCheck) || 0;

        // Only restore if recent (30 minutes)
        if (Date.now() - checkTime < 30 * 60 * 1000) {
          console.log('♻️ Restoring auth state from session storage');
          setAuthState({
            ...parsedState,
            user: parsedUser,
            userRole: savedRole,
            isLoading: false,
            initialized: true,
            ...parsedManagerInfo
          });
          return true;
        } else {
          console.log('⏰ Session expired, clearing storage');
          sessionStorage.clear();
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to restore auth state:', error);
      sessionStorage.clear();
    }
    
    // If no state was restored, check auth status
    checkAuthStatus();
    return false;
  };

  const isInRateLimitBackoff = useCallback(() => {
    if (rateLimitBackoff.current > Date.now()) {
      const remainingTime = Math.ceil((rateLimitBackoff.current - Date.now()) / 1000);
      console.log(`Rate limit backoff: ${remainingTime}s remaining`);
      return true;
    }
    return false;
  }, []);

  const setRateLimitBackoff = useCallback((duration = RATE_LIMIT_CONFIG.COOLDOWN_PERIOD) => {
    const backoffUntil = Date.now() + duration;
    rateLimitBackoff.current = backoffUntil;
    sessionStorage.setItem(STORAGE_KEYS.RATE_LIMIT_BACKOFF, backoffUntil.toString());
    console.log(`Rate limit backoff set until ${new Date(backoffUntil)}`);
  }, []);

  const persistAuthState = useCallback((state) => {
    // 🔥 REMOVED: No longer persisting state to sessionStorage
    console.log('⚠️ State persistence disabled - session will not persist across tabs');
  }, []);

  const clearPersistedAuth = useCallback(() => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        if (key !== STORAGE_KEYS.RATE_LIMIT_BACKOFF) {
          sessionStorage.removeItem(key);
        }
      });
      roleCache.current = null;
    } catch (error) {
      console.warn('Failed to clear persisted auth:', error);
    }
  }, []);

  const makeAuthenticatedRequest = useCallback(async (url, options = {}, retryCount = 0) => {
    // Don't make requests if logging out
    if (isLoggingOut.current) {
      throw new Error('LOGGING_OUT');
    }
    
    const maxRetries = RATE_LIMIT_CONFIG.MAX_RETRIES;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      console.log('Making authenticated request to:', url);

      const response = await fetch(url, {
        credentials: 'include',
        headers: { 
          'Accept': 'application/json', 
          'Content-Type': 'application/json'
        },
        signal: controller.signal,
        ...options
      });

      clearTimeout(timeoutId);

      if (response.status === 429) {
        console.warn(`Rate limited on ${url}`);
        setRateLimitBackoff();
        throw new Error('RATE_LIMITED');
      }

      return response;
      
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      
      if (error.message === 'RATE_LIMITED' || error.message === 'LOGGING_OUT') {
        throw error;
      }
      
      if (retryCount < maxRetries && !error.message.includes('401')) {
        const delay = Math.min(
          RATE_LIMIT_CONFIG.INITIAL_DELAY * Math.pow(RATE_LIMIT_CONFIG.BACKOFF_MULTIPLIER, retryCount),
          RATE_LIMIT_CONFIG.MAX_DELAY
        );
        
        console.log(`Retrying ${url} in ${delay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
        
        await new Promise(resolve => {
          const id = setTimeout(resolve, delay);
          retryTimeouts.current.push(id);
        });
        
        return makeAuthenticatedRequest(url, options, retryCount + 1);
      }
      
      throw error;
    }
  }, [setRateLimitBackoff]);

  const fetchUserRole = useCallback(async () => {
    if (isLoggingOut.current) {
      throw new Error('LOGGING_OUT');
    }
    
    if (isInRateLimitBackoff()) {
      if (roleCache.current && (Date.now() - roleCache.current.timestamp) < 30 * 60 * 1000) {
        console.log('Using cached role during backoff:', roleCache.current.role);
        return roleCache.current.role;
      }
      throw new Error('RATE_LIMITED');
    }

    try {
      console.log('Fetching user role');

      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/user-role`);

      if (response.ok) {
        const data = await response.json();
        console.log('Role response:', data);
        
        if (data.role) {
          roleCache.current = { role: data.role, timestamp: Date.now() };
          return data.role;
        }
        throw new Error('No role in response');
      } else if (response.status === 401) {
        console.log('User role fetch - not authenticated');
        return null;
      } else {
        throw new Error(`Role fetch failed: ${response.status}`);
      }
    } catch (error) {
      if (error.message === 'RATE_LIMITED' || error.message === 'LOGGING_OUT') {
        throw error;
      }
      
      console.error('Role fetch error:', error);
      
      if (roleCache.current && (Date.now() - roleCache.current.timestamp) < 10 * 60 * 1000) {
        console.log('Using cached role after error:', roleCache.current.role);
        return roleCache.current.role;
      }
      
      return null;
    }
  }, [isInRateLimitBackoff, makeAuthenticatedRequest]);

  const fetchUserData = useCallback(async () => {
    if (isLoggingOut.current) {
      throw new Error('LOGGING_OUT');
    }
    
    if (isInRateLimitBackoff()) {
      throw new Error('RATE_LIMITED');
    }

    try {
      console.log('Fetching user data with manager info');
      
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/user`);

      console.log('User fetch response:', response.status, response.statusText);

      if (!response.ok) {
        if (response.status === 401) {
          console.log('User not authenticated');
          return { authenticated: false };
        }
        throw new Error(`User fetch failed: ${response.status} - ${response.statusText}`);
      }

      const userData = await response.json();
      console.log('User data received:', userData);
      
      const managerInfo = {
        managerId: userData.manager_id || null,
        managerName: userData.manager_name || null,
        managerEmail: userData.manager_email || null,
        teamSize: userData.team_size || 0,
        canApproveLeaves: userData.can_approve_leaves || false
      };
      
      return { 
        authenticated: true, 
        user: userData,
        managerInfo: managerInfo
      };
      
    } catch (error) {
      if (error.message === 'RATE_LIMITED' || error.message === 'LOGGING_OUT') {
        throw error;
      }
      
      console.error('User fetch error:', error);
      return { authenticated: false, error: error.message };
    }
  }, [isInRateLimitBackoff, makeAuthenticatedRequest]);

  const checkAuthStatus = useCallback(async () => {
    // 🔥 CRITICAL: Don't check auth if logging out
    if (isLoggingOut.current) {
      console.log('⏭️ Skipping auth check - logout in progress');
      return false;
    }
    
    const now = Date.now();
    
    if (isInRateLimitBackoff()) {
      console.log('Skipping auth check - in rate limit backoff');
      setAuthState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: 'Rate limited - please wait before refreshing',
        initialized: true 
      }));
      return false;
    }

    if (authCheckInProgress.current || (now - lastAuthCheck.current) < RATE_LIMIT_CONFIG.CHECK_INTERVAL) {
      console.log('Auth check throttled');
      return false;
    }

    authCheckInProgress.current = true;
    lastAuthCheck.current = now;

    console.log('Starting auth status check...');

    try {
      setAuthState(prev => ({ 
        ...prev, 
        isLoading: true, 
        error: null 
      }));

      const userResult = await fetchUserData();

      if (!userResult.authenticated) {
        console.log('User not authenticated');
        const newState = { 
          isAuthenticated: false, 
          user: null, 
          userRole: null, 
          isLoading: false, 
          error: userResult.error || 'Not authenticated', 
          initialized: true,
          managerId: null,
          managerName: null,
          managerEmail: null,
          teamSize: 0,
          canApproveLeaves: false
        };
        setAuthState(newState);
        clearPersistedAuth();
        return false;
      }

      console.log('User authenticated, fetching role...');

      let userRole;
      try {
        userRole = await fetchUserRole();
      } catch (error) {
        if (error.message === 'RATE_LIMITED') {
          userRole = sessionStorage.getItem(STORAGE_KEYS.USER_ROLE) || 'Employee';
          console.log('Using cached role due to rate limit:', userRole);
        } else if (error.message === 'LOGGING_OUT') {
          throw error;
        } else {
          throw error;
        }
      }
      
      if (!userRole && userResult.user?.email === 'sriram.l@manurevasolutions.net') {
        console.log('Setting HR role for special user');
        userRole = 'HR';
      } else if (!userRole) {
        console.log('No role found, defaulting to Employee');
        userRole = 'Employee';
      }

      console.log('Final user role:', userRole);

      // 🔥 USER-SWITCH GUARD: if the server session now belongs to a DIFFERENT
      // user than the one this tab was showing, every page still holds the old
      // user's data in React state. Reload once so nothing of theirs survives.
      const prevUserId = lastUserIdRef.current;
      const newUserId = userResult.user?.id ?? null;
      lastUserIdRef.current = newUserId;
      if (prevUserId != null && newUserId != null && prevUserId !== newUserId) {
        console.warn(`👥 Session user changed ${prevUserId} -> ${newUserId}, reloading`);
        window.location.replace(ROLE_REDIRECTS[userRole] || '/Worker');
        return true;
      }

      const newAuthState = { 
        isAuthenticated: true, 
        user: userResult.user, 
        userRole, 
        isLoading: false, 
        error: null, 
        initialized: true,
        ...userResult.managerInfo
      };
      
      setAuthState(newAuthState);
      persistAuthState(newAuthState);

      // const shouldRedirect = location.pathname === '/' || location.pathname === '/';
      // if (shouldRedirect) {
      //   const redirectPath = ROLE_REDIRECTS[userRole] || '/';
      //   console.log('Redirecting to:', redirectPath);
      //   navigate(redirectPath, { replace: true });
      // }
      // 🔥 FIX: Don't redirect if we just logged out
      const urlParams = new URLSearchParams(window.location.search);
      const justLoggedOut = urlParams.get('logout') === 'success' || urlParams.get('logout') === 'true';

      const shouldRedirect = (location.pathname === '/' || location.pathname === '/') && !justLoggedOut;

      if (shouldRedirect) {
        const redirectPath = ROLE_REDIRECTS[userRole] || '/';
        console.log('Redirecting to:', redirectPath);
        navigate(redirectPath, { replace: true });
      } else if (justLoggedOut) {
        console.log('🚫 Skipping redirect - user just logged out');
        // Force clear the auth state even if backend says authenticated
        const clearedState = {
          isAuthenticated: false,
          user: null,
          userRole: null,
          isLoading: false,
          error: null,
          initialized: true,
          managerId: null,
          managerName: null,
          managerEmail: null,
          teamSize: 0,
          canApproveLeaves: false
        };
        setAuthState(clearedState);
        clearPersistedAuth();
        authCheckInProgress.current = false;
        return false;
      }

      return true;
      
    } catch (error) {
      if (error.message === 'LOGGING_OUT') {
        console.log('Auth check cancelled - logging out');
        return false;
      }
      
      console.error('Auth check failed:', error);
      
      let errorMessage = error.message;
      if (error.message === 'RATE_LIMITED') {
        errorMessage = 'Too many requests - please wait before refreshing';
      }
      
      setAuthState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: errorMessage, 
        initialized: true 
      }));
      return false;
    } finally {
      authCheckInProgress.current = false;
    }
  }, [location.pathname, navigate, fetchUserData, fetchUserRole, persistAuthState, clearPersistedAuth, isInRateLimitBackoff]);

  const refreshUserRole = useCallback(async () => {
    if (!authState.isAuthenticated || isLoggingOut.current) {
      console.log('Cannot refresh role - user not authenticated or logging out');
      return;
    }

    if (isInRateLimitBackoff()) {
      console.log('Cannot refresh role - in rate limit backoff');
      return;
    }

    console.log('Refreshing user role...');
    
    try {
      const newRole = await fetchUserRole();
      if (newRole && newRole !== authState.userRole) {
        console.log(`Role updated: ${authState.userRole} -> ${newRole}`);
        
        const updatedState = { ...authState, userRole: newRole };
        setAuthState(updatedState);
        persistAuthState(updatedState);
        
        const newRedirectPath = ROLE_REDIRECTS[newRole] || '/';
        const restrictedPaths = {
          '/SuperadminAttendance': ['Superadmin'],
          '/Attendance': ['CEO', 'Superadmin'],
          '/Hrdashboard': ['HR', 'CEO', 'Superadmin'],
          '/managerdashboard': ['Manager', 'HR', 'CEO', 'Superadmin']
        };
        
        const allowedRoles = restrictedPaths[location.pathname];
        if (allowedRoles && !allowedRoles.includes(newRole)) {
          console.log('Role change requires navigation to:', newRedirectPath);
          navigate(newRedirectPath, { replace: true });
        }
      }
    } catch (error) {
      console.error('Failed to refresh role:', error);
    }
  }, [authState, fetchUserRole, persistAuthState, navigate, location.pathname, isInRateLimitBackoff]);

  // 🔥 UPDATED: Proper logout handler with inactivity timer cleanup
  const handleLogout = useCallback(async () => {
    console.log('🚪 Logging out...');
    
    // Clear inactivity timer
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = null;
    }
    
    // Set logout flag immediately
    isLoggingOut.current = true;
    
    // Stop any ongoing operations
    retryTimeouts.current.forEach(clearTimeout);
    retryTimeouts.current = [];
    authCheckInProgress.current = false;
    
    // Clear state immediately
    setAuthState({ 
      isAuthenticated: false, 
      user: null, 
      userRole: null, 
      isLoading: false, 
      error: null, 
      initialized: true,
      managerId: null,
      managerName: null,
      managerEmail: null,
      teamSize: 0,
      canApproveLeaves: false
    });
    
    // Clear auth-specific storage
    clearPersistedAuth();
    sessionStorage.clear();
    
    // ✅ FIX: Preserve notification tracking in localStorage
    const notificationKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('viewed_notification_ids_')) {
        notificationKeys.push({
          key: key,
          value: localStorage.getItem(key)
        });
      }
    }
    
    // Clear localStorage
    localStorage.clear();
    
    // Restore notification tracking
    notificationKeys.forEach(({ key, value }) => {
      localStorage.setItem(key, value);
    });
    
    if (notificationKeys.length > 0) {
      console.log('🔔 Preserved notification tracking for', notificationKeys.length, 'user(s)');
    }
    
    // Clear cookies
    document.cookie.split(";").forEach(cookie => {
      const name = cookie.trim().split("=")[0];
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      const domain = window.location.hostname.split('.').slice(-2).join('.');
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${domain}`;
    });
    
    // Call backend logout
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${BACKEND_URL}/auth/logout`, { 
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        console.log('✅ Backend logout successful');
      } else {
        console.warn(`⚠️ Backend logout returned status ${response.status}`);
      }
    } catch (error) {
      console.warn('⚠️ Backend logout failed (non-critical):', error);
    }
    
    // Reset refs
    lastAuthCheck.current = 0;
    rateLimitBackoff.current = 0;
    roleCache.current = null;
    lastActivityTime.current = 0;
    lastUserIdRef.current = null;
    
    // Navigate to login
    console.log('➡️ Redirecting to login...');
    navigate('/', { replace: true });
    
    // Reset logout flag after a delay
    setTimeout(() => {
      isLoggingOut.current = false;
    }, 1000);
    
  }, [clearPersistedAuth, navigate]);

  const hasRole = useCallback((requiredRoles) => {
    if (!authState.userRole) return false;
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    return roles.includes(authState.userRole);
  }, [authState.userRole]);

  const hasAnyRole = useCallback((roles) => {
    if (!authState.userRole) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.some(role => authState.userRole === role);
  }, [authState.userRole]);

  const getRoleBasedRedirect = useCallback((role) => {
  console.log('🎯 [GET REDIRECT] Input role:', role);
  
  // 🔥 FIX: Normalize role to uppercase for matching
  const roleUpper = String(role || '').toUpperCase().trim().replace(/[\s_]+/g, '_');
  
  const roleRedirects = {
    'SUPERADMIN': '/Admindashboard',
    'CEO': '/Attendance',
    'ADMIN': '/Attendance',
    'HR': '/Hrdashboard',
    'MANAGER': '/managerdashboard',
    'EMPLOYEE': '/Worker',
    'CONSULTANT': '/Timesheet',  // ✅ CRITICAL FIX
    'NETWORK_MANAGER': '/NetworkManager',
    'HOUSEKEEPING': '/Worker'
  };
  
  const redirectPath = roleRedirects[roleUpper];
  
  if (!redirectPath) {
    console.warn('⚠️ [GET REDIRECT] Unknown role, defaulting to /Worker');
    return '/Worker';
  }
  
  console.log('✅ [GET REDIRECT] Redirect path:', redirectPath);
  return redirectPath;
}, []);

  const refreshAuth = useCallback(async () => {
    console.log('Full auth refresh requested...');
    
    if (isInRateLimitBackoff() || isLoggingOut.current) {
      console.log('Cannot refresh - in rate limit backoff or logging out');
      return false;
    }
    
    clearPersistedAuth();
    authCheckInProgress.current = false;
    roleCache.current = null;
    return await checkAuthStatus();
  }, [clearPersistedAuth, checkAuthStatus, isInRateLimitBackoff]);

  const isManager = useCallback(() => {
    return authState.canApproveLeaves || ['Manager', 'HR', 'CEO', 'Superadmin'].includes(authState.userRole);
  }, [authState.canApproveLeaves, authState.userRole]);

  const hasTeamMembers = useCallback(() => {
    return authState.teamSize > 0;
  }, [authState.teamSize]);

  const getManagerInfo = useCallback(() => {
    return {
      managerId: authState.managerId,
      managerName: authState.managerName,
      managerEmail: authState.managerEmail
    };
  }, [authState.managerId, authState.managerName, authState.managerEmail]);

  useEffect(() => {
    return () => {
      retryTimeouts.current.forEach(clearTimeout);
    };
  }, []);

  const clearRateLimit = useCallback(() => {
    rateLimitBackoff.current = 0;
    sessionStorage.removeItem(STORAGE_KEYS.RATE_LIMIT_BACKOFF);
    console.log('Rate limit backoff cleared');
  }, []);

  return {
    ...authState,
    checkAuthStatus,
    refreshAuth,
    refreshUserRole,
    handleLogout,
    hasRole,
    hasAnyRole,
    getRoleBasedRedirect,
    clearRateLimit,
    isInRateLimitBackoff: isInRateLimitBackoff(),
    isManager,
    hasTeamMembers,
    getManagerInfo,
    user: authState.user,
    role: authState.userRole,
    userRole: authState.userRole,
    loading: authState.isLoading,
    isLoading: authState.isLoading,
    authenticated: authState.isAuthenticated,
    isAuthenticated: authState.isAuthenticated,
    initialized: authState.initialized,
    error: authState.error,
    lastRoleCheck: lastAuthCheck.current,
    managerId: authState.managerId,
    managerName: authState.managerName,
    managerEmail: authState.managerEmail,
    teamSize: authState.teamSize,
    canApproveLeaves: authState.canApproveLeaves
  };
};

export { useAuth };
export default useAuth;

export const AuthProvider = ({ children }) => children;